/**
 * Theme diagnostic — collect runtime data to understand why Android MAX
 * WebView renders Mini App dark despite v1+v2 fixes.
 *
 * Behavior:
 *   - Collects diagnostic snapshot once after app mount.
 *   - console.log()'s the snapshot under a recognizable tag.
 *   - Exposes window.__themeDiagnostic for ad-hoc DevTools inspection.
 *   - Renders a fixed bottom-right overlay ONLY when:
 *       * URL search contains ?themeDebug=1
 *       * OR URL hash contains #themeDebug
 *     Never shown for regular users.
 *   - Never sends data to backend / external endpoint.
 *
 * Interpretation hint:
 *   - If computed body background is light (#ffffff) but device shows
 *     dark → compositor-level force-dark (Hypothesis B). v2 CSS fallback
 *     is the right direction but may need WebView-host fix from MAX.
 *   - If computed body background is dark (rgb < 100) → CSS / theme
 *     vars / SDK injection (Hypothesis A). The fix is in CSS.
 *
 * Wired in main.tsx: setTimeout(runThemeDiagnostic, 1000) after render.
 */

interface ThemeDiagnostic {
  timestamp: string;
  userAgent: string;
  // location.pathname (без query values — может содержать start_param с
  // city slug, нечувствительный, но не нужен в diagnostic).
  pathname: string;
  prefersColorScheme: 'light' | 'dark' | 'no-preference';
  html: {
    className: string;
    attributes: Record<string, string>;
    computedColorScheme: string;
    computedBackground: string;
    computedColor: string;
    computedFilter: string;
  };
  body: {
    className: string;
    attributes: Record<string, string>;
    computedColorScheme: string;
    computedBackground: string;
    computedColor: string;
    computedFilter: string;
  };
  root: {
    computedBackground: string;
    computedColor: string;
  };
  cssVariables: {
    colorBackground: string;
    colorTextPrimary: string;
    colorSurface: string;
    colorAccent: string;
    colorPrimary: string;
  };
  maxSdk: {
    webAppPresent: boolean;
    maxAppPresent: boolean;
    colorScheme: string | null;
    themeParams: unknown | null;
    platform: string | null;
    version: string | null;
  };
  interpretation: string;
}

const TAG = '[ThemeDiagnostic]';
let executed = false;

function getAttributes(el: Element | null): Record<string, string> {
  if (!el) return {};
  const result: Record<string, string> = {};
  for (const attr of Array.from(el.attributes)) {
    result[attr.name] = attr.value;
  }
  return result;
}

function detectPrefersColorScheme(): 'light' | 'dark' | 'no-preference' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'no-preference';
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return 'no-preference';
}

function parseRgb(value: string): { r: number; g: number; b: number } | null {
  // Matches rgb(R, G, B) или rgba(R, G, B, A) форматы которые возвращает
  // getComputedStyle. Хексы getComputedStyle не возвращает.
  const match = value.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return null;
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
}

function isDarkPixel(value: string): boolean {
  const rgb = parseRgb(value);
  if (!rgb) return false;
  // Brightness heuristic: average channel value < 100 = dark.
  return (rgb.r + rgb.g + rgb.b) / 3 < 100;
}

function buildInterpretation(d: ThemeDiagnostic): string {
  const bodyBgDark = isDarkPixel(d.body.computedBackground);
  const bodyFgDark = isDarkPixel(d.body.computedColor);
  if (bodyBgDark) {
    return 'Computed body background is DARK → CSS / theme variables / SDK injection (Hypothesis A). Fix should be in CSS cascade.';
  }
  if (!bodyBgDark && !bodyFgDark) {
    return 'Computed body background is LIGHT and text is also LIGHT → unusual; check #root background. May be transparent body with dark canvas underneath.';
  }
  return 'Computed body background is LIGHT, text is DARK (normal light theme at CSS level). If device shows DARK → compositor/WebView force-dark applied after CSS compute (Hypothesis B). v2 CSS fallback may help; if not, requires host-side fix in MAX WebView settings (Chromium setForceDark).';
}

function collect(): ThemeDiagnostic {
  const html = document.documentElement;
  const body = document.body;
  const root = document.getElementById('root');
  const htmlCs = window.getComputedStyle(html);
  const bodyCs = window.getComputedStyle(body);
  const rootCs = root ? window.getComputedStyle(root) : null;
  const rootStyles = window.getComputedStyle(html);

  // Read MAX SDK if present without throwing (defensive — types may not match).
  const w = window as unknown as {
    WebApp?: {
      colorScheme?: string;
      themeParams?: unknown;
      platform?: string;
      version?: string;
    };
    MaxApp?: unknown;
  };
  const webApp = w.WebApp;

  const data: ThemeDiagnostic = {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    pathname: window.location.pathname,
    prefersColorScheme: detectPrefersColorScheme(),
    html: {
      className: html.className,
      attributes: getAttributes(html),
      computedColorScheme: htmlCs.colorScheme || '(empty)',
      computedBackground: htmlCs.backgroundColor,
      computedColor: htmlCs.color,
      computedFilter: htmlCs.filter,
    },
    body: {
      className: body.className,
      attributes: getAttributes(body),
      computedColorScheme: bodyCs.colorScheme || '(empty)',
      computedBackground: bodyCs.backgroundColor,
      computedColor: bodyCs.color,
      computedFilter: bodyCs.filter,
    },
    root: {
      computedBackground: rootCs?.backgroundColor ?? '(no #root)',
      computedColor: rootCs?.color ?? '(no #root)',
    },
    cssVariables: {
      colorBackground: rootStyles.getPropertyValue('--color-background').trim() || '(unset)',
      colorTextPrimary: rootStyles.getPropertyValue('--color-text-primary').trim() || '(unset)',
      colorSurface: rootStyles.getPropertyValue('--color-surface').trim() || '(unset)',
      colorAccent: rootStyles.getPropertyValue('--color-accent').trim() || '(unset)',
      colorPrimary: rootStyles.getPropertyValue('--color-primary').trim() || '(unset)',
    },
    maxSdk: {
      webAppPresent: typeof webApp === 'object' && webApp !== null,
      maxAppPresent: typeof w.MaxApp !== 'undefined',
      colorScheme: webApp?.colorScheme ?? null,
      themeParams: webApp?.themeParams ?? null,
      platform: webApp?.platform ?? null,
      version: webApp?.version ?? null,
    },
    interpretation: '',
  };
  data.interpretation = buildInterpretation(data);
  return data;
}

// URL-gated overlay — показывается ТОЛЬКО при ?themeDebug=1 в search или
// #themeDebug в hash. После Phase 5 full dark theme support gate
// восстановлен — overlay для обычных пользователей не виден; нужен только
// для ad-hoc отладки если кто-то будет сообщать о визуальных проблемах
// с темой. Diagnostic infrastructure остаётся для console.log и
// window.__themeDiagnostic — они всегда работают.
function shouldShowOverlay(): boolean {
  if (typeof window === 'undefined') return false;
  const search = window.location.search ?? '';
  const hash = window.location.hash ?? '';
  return search.includes('themeDebug=1') || hash.includes('themeDebug');
}

function renderOverlay(data: ThemeDiagnostic): void {
  const overlay = document.createElement('div');
  overlay.id = '__theme-diagnostic-overlay';
  overlay.setAttribute('role', 'region');
  overlay.setAttribute('aria-label', 'Theme diagnostic');
  Object.assign(overlay.style, {
    position: 'fixed',
    right: '12px',
    bottom: '12px',
    maxWidth: '360px',
    width: 'calc(100vw - 24px)',
    maxHeight: '60vh',
    overflow: 'auto',
    background: '#ffffff',
    color: '#1a1a1f',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '8px 10px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: '11px',
    lineHeight: '1.35',
    zIndex: '2147483647',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  } satisfies Partial<CSSStyleDeclaration>);

  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.alignItems = 'center';
  header.style.justifyContent = 'space-between';
  header.style.marginBottom = '6px';
  header.style.fontWeight = '600';

  const title = document.createElement('span');
  title.textContent = 'Theme diagnostic';

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.textContent = '×';
  Object.assign(closeBtn.style, {
    background: 'transparent',
    border: 'none',
    fontSize: '18px',
    lineHeight: '1',
    cursor: 'pointer',
    padding: '0 4px',
    color: '#666',
  });
  closeBtn.onclick = () => overlay.remove();

  header.appendChild(title);
  header.appendChild(closeBtn);
  overlay.appendChild(header);

  const json = JSON.stringify(data, null, 2);

  // Textarea — Android long-press friendly для copy.
  const ta = document.createElement('textarea');
  ta.readOnly = true;
  ta.value = json;
  Object.assign(ta.style, {
    width: '100%',
    minHeight: '180px',
    boxSizing: 'border-box',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '6px 8px',
    fontFamily: 'inherit',
    fontSize: '11px',
    lineHeight: '1.35',
    background: '#fafafa',
    color: '#1a1a1f',
    resize: 'vertical',
  });
  overlay.appendChild(ta);

  // Copy button — try navigator.clipboard (modern Android Chromium WebView
  // supports it), fallback to textarea.select() для ручного long-press copy.
  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.textContent = 'Copy JSON';
  Object.assign(copyBtn.style, {
    marginTop: '6px',
    padding: '6px 10px',
    background: '#0a2475',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontFamily: 'inherit',
    fontSize: '11px',
    cursor: 'pointer',
    width: '100%',
  });
  copyBtn.onclick = async () => {
    try {
      // navigator.clipboard может быть недоступен в старых WebView или без
      // user-activation context. Тогда fallback на textarea.select().
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(json);
        copyBtn.textContent = 'Copied!';
      } else {
        ta.focus();
        ta.select();
        copyBtn.textContent = 'Select all (long-press → Copy)';
      }
    } catch {
      ta.focus();
      ta.select();
      copyBtn.textContent = 'Long-press text → Copy';
    }
    setTimeout(() => {
      copyBtn.textContent = 'Copy JSON';
    }, 2000);
  };
  overlay.appendChild(copyBtn);

  // Interpretation hint — visible вверху для быстрого визуала.
  const hint = document.createElement('div');
  hint.style.marginTop = '6px';
  hint.style.padding = '6px 8px';
  hint.style.background = '#fef3c7';
  hint.style.border = '1px solid #fde68a';
  hint.style.borderRadius = '6px';
  hint.style.color = '#92400e';
  hint.style.fontSize = '10.5px';
  hint.textContent = data.interpretation;
  overlay.appendChild(hint);

  document.body.appendChild(overlay);
}

/**
 * Run theme diagnostic. Idempotent (только один раз за жизнь страницы).
 * Безопасно вызывать в SSR / без window — early return.
 */
export function runThemeDiagnostic(): void {
  if (executed) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  executed = true;

  try {
    const data = collect();
    console.log(TAG, data);
    // Expose для DevTools ad-hoc inspection. Любой может проверить
    // window.__themeDiagnostic в Chrome DevTools / remote debug.
    (window as unknown as { __themeDiagnostic?: ThemeDiagnostic }).__themeDiagnostic = data;

    if (shouldShowOverlay()) {
      renderOverlay(data);
    }
  } catch (err) {
    // Diagnostic не должен ронять приложение никогда. Логируем и идём дальше.
    console.error(TAG, 'collect failed', err);
  }
}

export default runThemeDiagnostic;
